import { useCallback, useState } from 'react';

import { Alert, Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { FileRejection, useDropzone } from 'react-dropzone';

import { useAxios } from 'configs/requests';
import { Loading } from 'components/Fetch';
import { getApiErrorMessage } from 'utils/api-error';
import { migrationRoutes, parseReceiptPayload } from 'utils/PatientMigration';

interface ICompleteMigrationModalProps {
  /** The patient whose banner opened this. The receipt must be for them and no one else. */
  uid: string;
  onCancel: () => void;
  onCompleted: () => void;
}

/**
 * Completing the move on the source IS releasing its devices, and the receipt is what authorises
 * it: the release covers only the IMEIs the destination actually registered. This is the one
 * irreversible action in the ticket, so the receipt is uploaded rather than assumed.
 */
const CompleteMigrationModal = ({ uid, onCancel, onCompleted }: ICompleteMigrationModalProps) => {
  const { formatMessage } = useIntl();
  const [rejection, setRejection] = useState<string[]>([]);

  const [{ data, loading }, release] = useAxios<IMigrationReleaseResult>(
    { method: 'POST', url: migrationRoutes.releaseDevices },
    { manual: true }
  );
  // The server's own sentence, not a generic failure. A refusal here usually describes something
  // that went wrong a step EARLIER, such as a receipt naming no registered devices, and a generic
  // "something went wrong" sends the operator to inspect the file, the signature and the keys
  // rather than the registration that actually failed.
  const [refusal, setRefusal] = useState<string | null>(null);
  const [wrongReceipt, setWrongReceipt] = useState<string | null>(null);

  const onDropAccepted = useCallback(
    async (acceptedFiles: File[]) => {
      const receipt = acceptedFiles[0];
      if (!receipt) return;
      setRejection([]);
      setRefusal(null);
      setWrongReceipt(null);

      // Checked here because the server acts on the receipt's OWN sourceUid: it releases and
      // archives whoever the receipt names, not the patient whose banner this is. A valid receipt
      // for another patient dropped here would move that patient, and the step cannot be undone.
      // Unreadable fails closed for the same reason: a receipt we cannot attribute is one we
      // cannot say is this patient's.
      let payload: IMigrationReceiptPayload | null = null;
      try {
        payload = parseReceiptPayload(JSON.parse(await receipt.text()));
      } catch {
        payload = null;
      }
      if (!payload) {
        setWrongReceipt(formatMessage({ id: 'migrationReceiptUnreadableKey' }));
        return;
      }
      if (payload.sourceUid !== uid) {
        setWrongReceipt(formatMessage({ id: 'migrationReceiptWrongPatientKey' }));
        return;
      }

      const formData = new FormData();
      formData.append('file', receipt);
      try {
        // Deliberately NOT calling onCompleted here. The banner unmounts this modal when it
        // refreshes, so reporting completion at this point destroys the only account the
        // operator gets of the one irreversible step: what was released, what was retained and
        // whether the patient was archived. It is reported when they close.
        await release({ data: formData });
      } catch (caught) {
        setRefusal(getApiErrorMessage(caught, formatMessage({ id: 'migrationReleaseFailedKey' })));
      }
    },
    [release, formatMessage, uid]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected: (rejections: FileRejection[]) =>
      setRejection(rejections[0].errors.map((rejectionError) => rejectionError.message)),
    maxFiles: 1,
    multiple: false,
    accept: 'application/json,.json'
  });

  return (
    <Modal show backdrop="static" data-test="complete-migration-modal">
      <Modal.Header closeButton onHide={onCancel}>
        <Modal.Title>{formatMessage({ id: 'migrationCompleteTitleKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{formatMessage({ id: 'migrationCompleteIntroKey' })}</p>
        <Alert variant="warning" data-test="complete-migration-warning">
          {formatMessage({ id: 'migrationCompleteWarningKey' })}
        </Alert>

        {loading && <Loading />}
        {wrongReceipt && (
          <Alert variant="danger" data-test="complete-migration-wrong-receipt">
            {wrongReceipt}
          </Alert>
        )}
        {refusal && (
          <Alert variant="danger" data-test="complete-migration-refused">
            {refusal}
          </Alert>
        )}

        {!data && !loading && (
          <div
            {...getRootProps()}
            className={`migration-dropzone${isDragActive ? ' active' : ''}`}
            data-test="complete-migration-dropzone"
          >
            <input {...getInputProps()} data-test="complete-migration-file" />
            <p className="m-0">{formatMessage({ id: 'migrationReceiptDropzoneKey' })}</p>
          </div>
        )}

        {rejection.length > 0 && (
          <Alert variant="danger" data-test="complete-migration-rejected">
            {rejection.join(' ')}
          </Alert>
        )}

        {data && (
          <Alert variant="success" data-test="complete-migration-done">
            <div className="fw-bold mb-1">
              {formatMessage(
                { id: 'migrationCompleteDoneKey' },
                { count: data.released?.length ?? 0 }
              )}
            </div>
            {/* Retained devices are the reason the patient may still be active here, so they are
                stated rather than left to be inferred from the patient not being archived. */}
            {(data.retained?.length ?? 0) > 0 && (
              <div className="mb-1" data-test="complete-migration-retained">
                {formatMessage(
                  { id: 'migrationCompleteRetainedKey' },
                  { count: data.retained.length }
                )}
                <ul className="mb-0">
                  {data.retained.map((imei) => (
                    <li key={imei} className="font-monospace">
                      {imei}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div data-test="complete-migration-archived">
              {formatMessage({
                id: data.patientArchived
                  ? 'migrationCompleteArchivedKey'
                  : 'migrationCompleteNotArchivedKey'
              })}
            </div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="link"
          onClick={data ? onCompleted : onCancel}
          data-test="complete-migration-close"
        >
          {formatMessage({ id: data ? 'closeKey' : 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompleteMigrationModal;
